import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import axios from 'axios';
import { 
  CheckCircle, 
  Crown, 
  Sparkles,
  BookOpen,
  Loader,
  XCircle,
  ArrowRight
} from 'lucide-react';

const PaymentSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { user, API_BASE_URL } = useAuth();
  const [paymentStatus, setPaymentStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const navigate = useNavigate();

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setPaymentStatus('error');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/payments/status/${sessionId}`);
      setPaymentData(response.data);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        toast.success('Payment successful! Welcome to Premium!');
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
      } else {
        // Payment is still processing, keep checking
        setTimeout(checkPaymentStatus, 2000);
      }
    } catch (error) {
      setPaymentStatus('error');
      toast.error('Failed to verify payment status');
    }
  };

  const renderContent = () => {
    switch (paymentStatus) {
      case 'loading':
        return (
          <div className="text-center py-12">
            <Loader className="h-16 w-16 text-purple-400 mx-auto mb-6 animate-spin" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Processing Your Payment
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Please wait while we confirm your payment...
            </p>
            <div className="bg-gray-900/50 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-sm text-gray-400">
                This usually takes just a few seconds. Please don't close this page.
              </p>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="text-center py-12">
            <div className="bg-green-500/20 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-12 w-12 text-green-400" />
            </div>
            
            <h2 className="text-4xl font-bold text-white mb-4">
              Payment Successful!
            </h2>
            
            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-full inline-flex items-center space-x-2 mb-6">
              <Crown className="h-5 w-5" />
              <span className="font-semibold">Welcome to Premium!</span>
            </div>

            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Congratulations! Your account has been upgraded to Premium. You now have access to unlimited book generations and advanced features.
            </p>

            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-white mb-6">What's included in Premium?</h3>
              
              <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
                <div className="bg-gray-900/50 rounded-xl p-6 text-left">
                  <div className="flex items-center mb-4">
                    <Sparkles className="h-6 w-6 text-purple-400 mr-3" />
                    <h4 className="text-lg font-semibold text-white">Unlimited Generation</h4>
                  </div>
                  <p className="text-gray-300">
                    Create as many books as you want with no monthly limits.
                  </p>
                </div>
                
                <div className="bg-gray-900/50 rounded-xl p-6 text-left">
                  <div className="flex items-center mb-4">
                    <BookOpen className="h-6 w-6 text-purple-400 mr-3" />
                    <h4 className="text-lg font-semibold text-white">All Genres & Templates</h4>
                  </div>
                  <p className="text-gray-300">
                    Access to all book genres, templates, and formatting options.
                  </p>
                </div>
                
                <div className="bg-gray-900/50 rounded-xl p-6 text-left">
                  <div className="flex items-center mb-4">
                    <Sparkles className="h-6 w-6 text-purple-400 mr-3" />
                    <h4 className="text-lg font-semibold text-white">Advanced AI Models</h4>
                  </div>
                  <p className="text-gray-300">
                    Use the latest and most powerful AI models for better content.
                  </p>
                </div>
                
                <div className="bg-gray-900/50 rounded-xl p-6 text-left">
                  <div className="flex items-center mb-4">
                    <Crown className="h-6 w-6 text-purple-400 mr-3" />
                    <h4 className="text-lg font-semibold text-white">Priority Support</h4>
                  </div>
                  <p className="text-gray-300">
                    Get priority customer support and assistance.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <Link
                to="/generate"
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 flex items-center space-x-2"
              >
                <BookOpen className="h-5 w-5" />
                <span>Start Creating Books</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
              
              <Link
                to="/dashboard"
                className="bg-gray-700 text-white px-8 py-4 rounded-full font-semibold hover:bg-gray-600 transition-colors"
              >
                Go to Dashboard
              </Link>
            </div>
          </div>
        );

      case 'expired':
        return (
          <div className="text-center py-12">
            <div className="bg-yellow-500/20 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-12 w-12 text-yellow-400" />
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-4">
              Payment Session Expired
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              Your payment session has expired. Please try again to upgrade to Premium.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/dashboard"
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105"
              >
                Try Again
              </Link>
              
              <Link
                to="/"
                className="bg-gray-700 text-white px-8 py-4 rounded-full font-semibold hover:bg-gray-600 transition-colors"
              >
                Back to Home
              </Link>
            </div>
          </div>
        );

      case 'error':
      default:
        return (
          <div className="text-center py-12">
            <div className="bg-red-500/20 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-12 w-12 text-red-400" />
            </div>
            
            <h2 className="text-3xl font-bold text-white mb-4">
              Payment Verification Failed
            </h2>
            
            <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
              We couldn't verify your payment. If you were charged, please contact support.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105"
              >
                Retry Verification
              </button>
              
              <Link
                to="/dashboard"
                className="bg-gray-700 text-white px-8 py-4 rounded-full font-semibold hover:bg-gray-600 transition-colors"
              >
                Go to Dashboard
              </Link>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="bg-gray-900/50 backdrop-blur-sm rounded-3xl p-8 border border-purple-500/20">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccessPage;